'use client';

import * as React from 'react';

import { cn } from './cn';

export type LinkUnderline = 'always' | 'hover' | 'none';
export type LinkTone = 'primary' | 'inherit' | 'muted';

export interface LinkProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'color'> {
  /**
   * The target, and REQUIRED — see the module note's "WHY `href` IS REQUIRED".
   * A link-shaped element without one is not interactive: it takes accent ink and
   * a focus ring it can never show, while the base pointer contract deliberately
   * leaves `a:not([href])` without a hand (tailwind.css). MUI's `Link` allowed
   * `component="button"` with no `href`, and that call site migrates to `Button`,
   * `linkClasses()` on its own renderer, or a real anchor — never to a dead one.
   */
  href: string;
  /**
   * MUI 6's `underline`. Default `always`, which is MUI's own default and the
   * reason the 31-call-site sweep is behaviour-preserving.
   */
  underline?: LinkUnderline;
  /** Which ink the link wears. Default `primary`. */
  tone?: LinkTone;
  /**
   * An off-origin link: adds `target="_blank"` and the matching `rel`. Kept as
   * one prop rather than two because a `target="_blank"` without
   * `rel="noreferrer"` is a tabnabbing hole, and every call site that wanted
   * one wanted the other.
   */
  external?: boolean;
}

/**
 * The new-system link: the MUI-free replacement for `@mui/material/Link`
 * (31 files).
 *
 * WHY THE CLASSES ARE A NAMED EXPORT AND NOT ONLY A COMPONENT. In this app a
 * link is not always an `<a>`: auth pages compose MUI's `Link` with
 * `react-router`'s `RouterLink` (`component={RouterLink} to="/login"`) and
 * most of the product composes it with `next/link` (`component={Link}
 * href=…`). Neither router's `Link` is a `<a>` this file can render, and
 * routing belongs to the router — but the APPEARANCE must not fork. So the
 * one source of truth is `linkClasses()`, and the SPA call site is
 * `<NextLink className={linkClasses({ underline, tone })}>`. A second spelling
 * of the link's ink is exactly the drift this migration exists to remove; a
 * second RENDERER is not.
 *
 * THE UNDERLINE DEFAULTS TO `always`, AND THAT IS A CORRECTION. This file first
 * shipped `hover`, on the belief that MUI 6 hover-underlined by default. It does
 * not: MUI 6.5.0's `Link` reads `underline = 'always'` (`Link/Link.js:169`), so
 * every one of the 31 files that imports `@mui/material/Link` today is
 * underlined at rest and a mechanical sweep would have silently removed the
 * affordance from all of them (design review round 1, D1). Colour alone cannot
 * carry it either: at rest the light-mode link ink and the body ink are 1.02:1
 * apart, so an un-underlined link loses its only WCAG 1.4.1 cue. `hover` and
 * `none` remain opt-ins.
 *
 * WHY `href` IS REQUIRED. Not cosmetic: this component's whole look is the
 * "live interactive element" treatment, and it is the base contract that grants
 * the hand only to `a[href]`. With `href` optional the component could render
 * accent ink, a focus ring and no cursor — the exact "looks live, is not"
 * failure the pointer contract exists to prevent (code review round 1, MINOR).
 *
 * WHY `tone` DEFAULTS TO `primary` AND `inherit` EXISTS. MUI `Link` paints
 * `text.primary` — the accent — by default, and the tree relies on that. But a
 * link inside an `Alert`, a `Card` heading or a muted paragraph must take the
 * surrounding ink, and MUI's `color="inherit"` was the escape hatch. Both are
 * kept, plus `muted` for the `text.secondary` tier the new `ui/text` scale
 * names.
 *
 * THE HAND CURSOR IS NOT SET HERE. `a[href]` is a live interactive element, so
 * the one base-layer contract in `src/tailwind.css` paints its hand (and
 * `not-allowed` when `aria-disabled`). A `cursor-pointer` utility here would be
 * a second, silently-losing spelling of a rule that already exists — see
 * `src/components/ui/interactive-cursor.test.ts`.
 *
 * FOCUS is the house ring (2px `--color-ring`, 2px offset) with a small radius
 * so the ring follows an inline run of text instead of a giant box around it.
 */
export function linkClasses({
  underline = 'always',
  tone = 'primary',
  className,
}: {
  underline?: LinkUnderline;
  tone?: LinkTone;
  className?: string;
} = {}) {
  return cn(
    'minerva-link rounded-[2px] transition-colors focus-visible:[outline:2px_solid_var(--color-ring)] focus-visible:outline-offset-2',
    tone === 'primary' && 'text-primary-text',
    tone === 'muted' && 'text-muted-foreground',
    // `inherit` emits no ink class: the link takes whatever its container
    // paints, which is what MUI's `color="inherit"` meant.
    underline === 'always' && 'underline',
    underline === 'hover' && 'hover:underline',
    underline === 'none' && 'no-underline',
    className
  );
}

/**
 * A presentational anchor. It renders a real `<a>` and forwards every anchor
 * attribute, so `href`, `download`, `referrerPolicy` and `data-*` all work as
 * they did. It deliberately does NOT take a router prop (`to`) — see the
 * module note for how a router-backed link wears the same classes.
 */
export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  function Link(
    {
      className,
      external = false,
      rel,
      target,
      tone = 'primary',
      underline = 'always',
      ...rest
    },
    ref
  ) {
    return (
      <a
        ref={ref}
        className={linkClasses({ underline, tone, className })}
        rel={external ? (rel ?? 'noreferrer') : rel}
        target={external ? (target ?? '_blank') : target}
        {...rest}
      />
    );
  }
);
Link.displayName = 'Link';
